/**
 * Checkout section wrapper — heading + optional divider above a block of
 * checkout content (payment details, waiver, etc).
 *
 * @module pages/booking/components/CheckoutSection
 */

import { useId, type ReactNode } from 'react';

export interface CheckoutSectionProps {
  title: string;
  /** 1px rule above the heading. Off by default so a first section has none. */
  divider?: boolean;
  headingTestId?: string;
  /** Heading classes. Default is the booking checkout heading. */
  headingClassName?: string;
  className?: string;
  'data-testid'?: string;
  children: ReactNode;
}

export function CheckoutSection({
  title,
  divider = false,
  headingTestId,
  headingClassName = 'text-xl font-semibold text-fg-secondary',
  className = '',
  'data-testid': dataTestId,
  children,
}: CheckoutSectionProps) {
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      data-testid={dataTestId}
      className={`space-y-6 ${divider ? 'border-t border-butter-gray-200 pt-8' : ''} ${className}`}
    >
      <h2
        id={headingId}
        className={headingClassName}
        data-testid={headingTestId}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}
